import type { SizeMm } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useStore } from 'zustand';
import {
  findDriftedRealSize,
  measurementFrameOf,
  useEditAreaDraftStore,
} from '@/entities/edit-area';
import { formatMm, formatMmChange } from '@/shared/lib';
import { Button } from '@/shared/ui';

/**
 * 원통 상품의 변경 문구. 저장 프레임과 새 프레임(π × 지름 / 2, 높이)은 불일치 판정과 같은 허용오차로 비교해,
 * 다른데 같아 보이면 자리를 늘려 보인다(formatMmChange). 평면 상품은 실측 원값을 그대로 보이므로 이 규칙을 쓰지 않는다.
 */
const describeCylinderChange = (stored: SizeMm, frame: SizeMm, diameterMm: number): string => {
  const [storedWidth, frameWidth] = formatMmChange(stored.widthMm, frame.widthMm);
  const [storedHeight, frameHeight] = formatMmChange(stored.heightMm, frame.heightMm);
  return `상품 실측이 바뀌었습니다. 프레임 ${storedWidth} → ${frameWidth}mm (지름 ${formatMm(diameterMm)}), 높이 ${storedHeight} → ${frameHeight}mm.`;
};

/**
 * 저장된 표면 실측이 지금의 상품 실측 프레임과 다를 때 — 운영자가 기본정보에서 실측을 고쳤거나,
 * 옛 실측으로 저장된 버전을 되돌리기로 열었을 때 — 명시적 갱신을 요구한다. 자동으로 바꾸지 않는다.
 * 원통 상품은 프레임 폭(π × 지름 / 2)끼리 보이고 지름은 괄호로 덧붙인다 — 프레임 폭과 지름을 나란히 두지 않는다.
 */
export const MeasurementDriftNotice = (): ReactElement | null => {
  const state = useStore(useEditAreaDraftStore());
  const { measurement, measurementKind } = state;
  const stored = findDriftedRealSize(state.faces, measurement, measurementKind);
  if (measurement === null || stored === null) return null;

  const change =
    measurementKind === 'CYLINDER'
      ? describeCylinderChange(
          stored,
          measurementFrameOf(measurementKind, measurement),
          measurement.widthMm,
        )
      : `상품 실측이 ${stored.widthMm} × ${stored.heightMm} → ${measurement.widthMm} × ${measurement.heightMm} mm로 바뀌었습니다.`;

  return (
    <section
      role="alert"
      aria-label="상품 실측 변경"
      className="space-y-2 rounded-lg border border-amber-500 p-4 text-sm"
    >
      <p>
        {change} 실측을 갱신하면 표면 실측이 새 값으로 바뀌고 배치 범위를 다시 검사합니다. 갱신 후
        저장하면 새 버전이 됩니다.
      </p>
      <Button
        type="button"
        disabled={state.locked || state.isUnsupported}
        onClick={() => state.refreshSurfaceRealSize()}
      >
        실측 갱신
      </Button>
    </section>
  );
};
